import type { CustomScatterChartAxisType, ReportPageScatterChartType, StudentPageScatterChartType } from '../types';
import { APP_THEME } from '../../../styles/theme/styled/theme';

export const CHARTS_ANIMATION_DURATION = 2000;
export const TOPIC_PROGRESS_CHARTS_ANIMATION_DURATION = 3000;
export const TOOLTIP_WIDTH = 150;

export const STUDENT_PROGRESS_MIN_HEIGHT = 50;
export const STUDENT_PROGRESS_MAX_HEIGHT = 50;
export const STUDENT_PROGRESS_AXIS_HEIGHT = 10;
export const STUDENT_PROGRESS_AXIS_OFFSET = 20;
export const STUDENT_PROGRESS_TICK_WIDTH = 4;
export const STUDENT_PROGRESS_TICK_HEIGHT = 20;
export const STUDENT_PROGRESS_TICK_PADDING = 10;
export const STUDENT_PROGRESS_BREAK_WIDTH = 3;
export const STUDENT_PROGRESS_BREAK_HEIGHT = 15;
export const STUDENT_PROGRESS_SMILEY_SIZE = 29;
export const STUDENT_PROGRESS_TICK_NUMBER = 3;
export const STUDENT_PROGRESS_VERTICAL_LINE_WIDTH = 6;
export const STUDENT_PROGRESS_MARGINS = {
  top: 10,
  right: 50,
  bottom: 30,
  left: 50,
};
// Maths-age:
export const STUDENT_PROGRESS_THIN_AXIS_HEIGHT = STUDENT_PROGRESS_AXIS_HEIGHT / 2;

// For labels:
export const PROGRESS_LABEL_MAX_WIDTH = 60;
export const PROGRESS_LABEL_HEIGHT = 30;
export const PROGRESS_LABEL_ROUNDED = 5;
export const PROGRESS_LABEL_STROKE_WIDTH = 2;
export const PROGRESS_LABEL_HIGHLIGHT_STROKE_WIDTH = 10;
export const PROGRESS_LABEL_MARKER_RADIUS = 7;
export const PROGRESS_LABEL_OFFSET = 15;
export const PROGRESS_LABEL_FONTSIZE = '1rem';

// !! PLEASE Do NOT change these values unless you update the path of the icons
// Student svg attributes are width="28" height="44" viewBox="0 0 20 32"
// Highlighted student svg attributes are width="34" height="53" viewBox="0 0 25 40"
// Class svg attributes are width="30" height="30" viewBox="0 0 30 30"
// Highlighted class svg attributes are width="35" height="40" viewBox="0 0 35 35"
export const STUDENT_ICON_WIDTH = 28;
export const STUDENT_ICON_HEIGHT = 44;
export const HIGHLIGHTED_STUDENT_ICON_WIDTH = 34;
export const HIGHLIGHTED_STUDENT_ICON_HEIGHT = 53;
export const YEAR_GROUP_ICON_WIDTH = 60;
export const YEAR_GROUP_ICON_HEIGHT = 30;
export const YEAR_GROUP_ICON_HIGHLIGHTED_WIDTH = 65;
export const YEAR_GROUP_ICON_HIGHLIGHTED_HEIGHT = 35;

export const STUDENT_PROGRESS_CLASSES: {
  element: string,
  icon: string,
  value: string,
  cursor: string,
} = {
  element: 'scatter-element',
  icon: 'data-icon',
  value: 'data-value',
  cursor: 'cursor',
};

export const TOPIC_PROGRESS_BAR_HEIGHT = 35;
export const TOPIC_PROGRESS_FIXED_MIN = 5.0;
export const TOPIC_PROGRESS_CIRCLE_RADIUS = 5;
export const TOPIC_PROGRESS_STROKE_WIDTH = 0.1;
export const TOPIC_PROGRESS_AVERAGE_WIDTH = 3;
export const TOPIC_PROGRESS_AVERAGE_DASHARRAY = 5;
export const TOPIC_PROGRESS_FILTERED_ARROW_STROKE = 2;
export const TOPIC_PROGRESS_LABELS_WIDTH_PERCENT = 33.33;
export const TOPIC_PROGRESS_CHEVRON_UP = '\u2303';
export const TOPIC_PROGRESS_CHEVRON_DOWN = '\u2304';
export const TOPIC_PROGRESS_LABEL_SIZE = '1.1rem';
export const TOPIC_PROGRESS_TITLE_OFFSET = 35;

export const TOPIC_PROGRESS_CLASSES: {
  row: string,
  grid: string,
  label: string,
  chevron: string,
  student: string
} = {
  row: 'topic_progress_chart-row',
  grid: 'topic_progress_chart-grid',
  label: 'topic_progress_chart-label',
  chevron: 'topic_progress_chart-chevron',
  student: 'topic_progress_chart-student',
};

export const TOPIC_PROGRESS_GRADIENT_ID = 'svgGradient';
export const TOPIC_PROGRESS_GRADIENT_FILTERED_ID = 'svgGradient_filtered';
export const TOPIC_PROGRESS_GRADIENT_ID_REVERSE = 'svgGradient_reverse';
export const TOPIC_PROGRESS_GRADIENT_FILTERED_ID_REVERSE = 'svgGradient_filtered_reverse';

export const TOPIC_PROGRESS_COLORS = {
  green_gradient_base: APP_THEME.colors.positive,
  green_gradient_light: '#EDF7D6',
  blue_gradient_base: '#14BAEE',
  blue_gradient_light: '#F5FCFE',
  bar_border: APP_THEME.colors.primary,
  bar_light: '#F2F4F7',
  red: '#ED1B35',
  blue: APP_THEME.colors.primary,
};

export const SCATTER_CHART_STROKE_WIDTH = 0.3;

export const SCATTER_CHART_COLORS = {
  negative: APP_THEME.colors.negative,
  good: '#f78c23',
  positive: APP_THEME.colors.positive,
  zero: APP_THEME.colors.dark,
  highlight: APP_THEME.colors.primary,
};

export const GOOD_BREAKING_VALUES: Record<StudentPageScatterChartType | ReportPageScatterChartType, number | null> = {
  progression: 2,
  qualityLearningTime: 15,
  mathsAgeImprovement: null,
  mathsAge: null,
  mathsAgeDelta: null,
  initialAssessment: null,
};

export const EXCELLENT_BREAKING_VALUES: Record<StudentPageScatterChartType | ReportPageScatterChartType, number> = {
  progression: 3,
  qualityLearningTime: 30,
  mathsAgeImprovement: 8,
  mathsAge: 5.5,
  mathsAgeDelta: 0,
  initialAssessment: 0,
};

export const EXCESSIVE_BREAKING_VALUES: Record<StudentPageScatterChartType | ReportPageScatterChartType, number | null> = {
  progression: null,
  qualityLearningTime: 90,
  mathsAgeImprovement: null,
  mathsAge: null,
  mathsAgeDelta: null,
  initialAssessment: null,
};

export const FIXED_MAX_VALUES: Record<StudentPageScatterChartType | ReportPageScatterChartType, number | null> = {
  progression: 5,
  qualityLearningTime: 120,
  mathsAgeImprovement: null,
  mathsAge: null,
  mathsAgeDelta: null,
  initialAssessment: 100,
};

const qualityLearningTime = {
  max: 90,
  threshold: 300,
  tickValues: [ 0, 15, 30, 60, 90 ],
  tickValuesOverThreshold: [ 0, 30, 90 ],
  highlight: [ 30 ],
};

export const FIXED_AXIS: Record<'recent' | 'last_weeks', Record<ReportPageScatterChartType, CustomScatterChartAxisType> | null> = {
  recent: {
    progression: null,
    qualityLearningTime: qualityLearningTime,
    mathsAgeImprovement: null,
    mathsAge: null,
    mathsAgeDelta: null,
    initialAssessment: null,
  },
  last_weeks: {
    progression: null,
    qualityLearningTime: qualityLearningTime,
    mathsAgeImprovement: {
      max: 16,
      tickValues: [ 0, 4, 8, 12 ],
      highlight: [ 8 ],
      showUnitOnHighlightOnly: true,
    },
    mathsAge: null,
    mathsAgeDelta: null,
    initialAssessment: null,
  },
};

export const NO_DOTTED_LINE_CHARTS: ReportPageScatterChartType[] = [ 'mathsAgeDelta', 'mathsAgeImprovement', 'initialAssessment', 'mathsAge', 'progression' ];
